import { ArrowLeft, ArrowRight } from "lucide-react";
import React, { useEffect, useMemo } from "react";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgDialog,
  IgDivider,
  IgTooltip,
} from "@intelligems/ig-theme";
import { LivePriceDto, OnboardingInfoFe } from "@intelligems/ig-types";
import { CircularProgress, Grid, useTheme } from "@mui/material";
import { usePatchConfigurations } from "@src/api/configurations/patch-configurations";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import {
  useCurrentPriceVersion,
  useGetLivePrices,
} from "@src/api/intelligems/get-live-prices";
import { useGetOnboardingInfo } from "@src/api/organization-actions/get-onboarding-info";
import { usePostOnboardingInfo } from "@src/api/organization-actions/post-onboarding-info";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useAdjustBilling } from "@src/api/stripe/adjust-billing";
import { useLoggedInUser } from "@src/api/user-auths/login";
import { usePatchUser } from "@src/api/users/patch-user";
import { useCheckSubscriptionStatus } from "@src/components/LimitAccess/check-subscriptions-hooks";
import { OnboardingBillingPlan } from "@src/components/MainApp/Onboarding/pages/OnboardingBillingPlan";
import { OnboardingGoalsInfo } from "@src/components/MainApp/Onboarding/pages/OnboardingGoalsInfo";
import { OnboardingSalesInfo } from "@src/components/MainApp/Onboarding/pages/OnboardingSalesInfo";
import { OnboardingStoreInfo } from "@src/components/MainApp/Onboarding/pages/OnboardingStoreInfo";
import { OnboardingUserInfo } from "@src/components/MainApp/Onboarding/pages/OnboardingUserInfo";
import {
  getMixpanelStepData,
  getMixpanelStepTitle,
  getOnboardingInfoPayload,
  getOrderVolumeValue,
  isContinueDisabled,
} from "@src/components/MainApp/Onboarding/utils";
import { useDispatch, useSelector } from "@src/store";
import { isDialogOpen, setDialog } from "@src/store/slices/global";
import { mpOnboardingStepComplete } from "@src/utils/mixpanel";

import { OnboardingHeaderStepper } from "./components/OnboardingHeaderStepper";

export const OnboardingDialog = () => {
  const user = useLoggedInUser();
  const patchUser = usePatchUser();
  const postOnboardingInfo = usePostOnboardingInfo();
  const org = useGetOrg();

  const dispatch = useDispatch();
  const livePrices = useGetLivePrices();
  const priceVersion = useCurrentPriceVersion();
  const mutateConfigurations = usePatchConfigurations();
  const onboardingTestFlow = useConfiguration("onboarding_test_flow");
  const isOpen = useSelector(isDialogOpen("onboarding"));
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useImmer(0);
  const existingOnboardingInfo = useGetOnboardingInfo();
  const [completedStepsMixpanel, setCompletedStepsMixpanel] = useImmer<
    number[]
  >([]);
  const isSubscriptionActive = useCheckSubscriptionStatus();
  const adjustBilling = useAdjustBilling(true);
  const [data, setData] = useImmer<OnboardingInfoFe>({
    preferredName: "",
    preferredEmail: "",
    companyRole: null,
    otherRole: "",
    teamDescription: null,
    agencyPrimaryOperator: null,
    previousExperience: null,
    storeType: "standard",
    userReportedOrderCount: "I don't know",
    productionStoreUrl: "",
    goals: [],
    focus: null,
    recommendedPlan: null,
    recommendedLivePlan: null,
    buyingIntention: null,
    otherBuyingIntention: "",
    sourceAttribution: null,
    otherSourceAttribution: "",
  });

  useEffect(() => {
    if (user.isSuccess) {
      setData({
        ...data,
        preferredEmail: user.data.user.email,
        preferredName: `${user.data.user.firstName} ${user.data.user.lastName}`,
        userReportedOrderCount: getOrderVolumeValue(
          org.data.org.orderCount || 0
        ),
      });
    }
  }, [user.isSuccess]);

  const priceData = useMemo(() => {
    if (!livePrices.isSuccess) {
      return [];
    }

    return livePrices.data.prices.filter(
      (p) => p.product.name === "Core" || p.product.name === "Plus"
    );
  }, [livePrices.isSuccess, livePrices.data]);

  const handleCloseDialog = () => {
    dispatch(setDialog({ dialog: "onboarding", open: false }));
  };

  const handleChange = (
    field: keyof OnboardingInfoFe,
    value: string | null | undefined | string[] | LivePriceDto
  ) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const onComplete = (bypassOnboardingInfo?: boolean) => {
    if (user.isSuccess && org.isSuccess) {
      if (!bypassOnboardingInfo) {
        postOnboardingInfo.mutate({
          body: getOnboardingInfoPayload(data),
          pathParams: undefined,
          queryParams: undefined,
        });
        mutateConfigurations.mutate([
          {
            ...onboardingTestFlow,
            value: data.goals.includes("Set a Custom Goal")
              ? "content"
              : data.goals.includes("Tweak Shipping Strategy")
                ? "shipping"
                : data.goals.includes("Optimize Product Pricing")
                  ? "pricing"
                  : "content",
          },
        ]);

        if (data.recommendedLivePlan) {
          adjustBilling.mutate({
            pathParams: undefined,
            queryParams: undefined,
            body: {
              couponId: undefined,
              version: data.recommendedLivePlan.product.version,
              action: "create",
              priceIds: data.recommendedLivePlan.priceIds,
            },
          });
        }
      }
      patchUser.mutate({
        queryParams: undefined,
        pathParams: { id: user.data.user.id },
        body: {
          user: {
            organizationId: org.data.org.id,
            id: user.data.user.id,
            showWelcome: false,
            preferredName: data.preferredName,
            preferredEmail: data.preferredEmail,
            companyRole:
              data.companyRole === "Other" ? data.otherRole : data.companyRole,
            previousExperience: data.previousExperience,
            email: user.data.user.email,
          },
        },
      });
      handleCloseDialog();
    }
  };

  const isContinueDisabledWrapper = useMemo(() => {
    return isContinueDisabled(data, currentStep);
  }, [currentStep, data]);

  const onContinueClick = () => {
    if (
      existingOnboardingInfo.data !== null &&
      existingOnboardingInfo.data !== undefined
    ) {
      mpOnboardingStepComplete(
        getMixpanelStepTitle(currentStep),
        currentStep,
        getMixpanelStepData(data, currentStep)
      );
      onComplete(true);
    } else if (
      (currentStep === 3 && isSubscriptionActive) ||
      priceVersion !== "v3"
    ) {
      onComplete(false);
    } else if (currentStep === 4) {
      onComplete(false);
    } else {
      if (!completedStepsMixpanel.includes(currentStep)) {
        setCompletedStepsMixpanel((prev) => {
          prev = [...prev, currentStep];
          return prev;
        });
        mpOnboardingStepComplete(
          getMixpanelStepTitle(currentStep),
          currentStep,
          getMixpanelStepData(data, currentStep)
        );
      }
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <IgDialog
      minWidth={"736px"}
      dialogContentPadding={"0px"}
      dialogContentWidth={"100%"}
      padding={"32px"}
      disableEscapeKeyDown={true}
      title={null}
      hideActions={true}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: theme.igBackgroundColors.baseLightMid,
        },
        "& .MuiPaper-root": { boxShadow: theme.igShadows.low },
        marginLeft: "0px !important",
      }}
      handleClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleCloseDialog();
        }
      }}
      showDialog={isOpen}
      rawContent={
        existingOnboardingInfo.isLoading || org.isLoading ? (
          <Grid
            container
            direction={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            sx={{ height: "100px" }}
          >
            <CircularProgress color={"info"} />
          </Grid>
        ) : (
          <Grid container rowSpacing={8}>
            {currentStep !== 4 && !existingOnboardingInfo.data ? (
              <Grid item xs={12}>
                <OnboardingHeaderStepper currentStep={currentStep} />
              </Grid>
            ) : null}
            <Grid item>
              {currentStep === 0 ? (
                <OnboardingUserInfo data={data} onChange={handleChange} />
              ) : currentStep === 1 ? (
                <OnboardingStoreInfo
                  data={data}
                  onChange={handleChange}
                  storeUrl={org.data.org.domain || org.data.org.shopId || ""}
                />
              ) : currentStep === 2 ? (
                <OnboardingGoalsInfo data={data} onChange={handleChange} />
              ) : currentStep === 3 ? (
                <OnboardingSalesInfo data={data} onChange={handleChange} />
              ) : (
                <OnboardingBillingPlan
                  data={data}
                  priceData={priceData}
                  orgOrderCount={org.data.org.orderCount || 0}
                  onClose={onComplete}
                  onChange={handleChange}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <IgDivider spacing={"l"} />
            </Grid>
            <Grid item container justifyContent={"space-between"}>
              <Grid item>
                {currentStep !== 0 && (
                  <IgButton
                    onClick={() => {
                      setCurrentStep((prev) => prev - 1);
                    }}
                    startIcon={<ArrowLeft size={16} />}
                    color={"secondary"}
                  >
                    Back
                  </IgButton>
                )}
              </Grid>
              <Grid item>
                <IgTooltip title={isContinueDisabledWrapper.reason}>
                  <IgButton
                    disabled={isContinueDisabledWrapper.isDisabled}
                    onClick={onContinueClick}
                    endIcon={
                      <ArrowRight
                        size={16}
                        color={
                          isContinueDisabledWrapper.isDisabled
                            ? theme.igBorders.core.active
                            : theme.igIconColors.dark
                        }
                      />
                    }
                  >
                    {currentStep === 4 ||
                    (existingOnboardingInfo.data !== null &&
                      existingOnboardingInfo.data !== undefined)
                      ? "Get Started"
                      : "Continue"}
                  </IgButton>
                </IgTooltip>
              </Grid>
            </Grid>
          </Grid>
        )
      }
    />
  );
};
